<script setup lang="ts">
import type { Component } from 'vue';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-vue-next';
import { formatDate } from '~/lib/dateFormat';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

withDefaults(defineProps<{
    title: string;
    summary?: string;
    scopeLabel?: string;
    version?: number | string | null;
    status: string;
    statusVariant?: BadgeVariant;
    updatedAt?: unknown;
    icon?: Component;
}>(), {
    summary: '', scopeLabel: '', version: null, statusVariant: 'outline', updatedAt: undefined, icon: () => FileText,
});
</script>

<template>
    <div class="group/contract flex items-start gap-3 px-1 py-4 first:pt-1 last:pb-1">
        <div class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <component :is="icon" class="size-4" />
        </div>

        <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                <div class="min-w-0">
                    <p class="text-sm font-semibold text-foreground">{{ title }}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                        <template v-if="scopeLabel">{{ scopeLabel }}</template><template v-if="version !== null"> · Version {{ version }}</template><template v-if="updatedAt"> · Updated {{ formatDate(updatedAt) }}</template>
                    </p>
                </div>

                <div class="flex shrink-0 items-center gap-1.5">
                    <Badge :variant="statusVariant">{{ status }}</Badge>
                    <slot name="menu" />
                </div>
            </div>

            <p v-if="summary" class="mt-2 line-clamp-2 text-sm leading-5 text-muted-foreground">{{ summary }}</p>
            <slot name="details" />
            <slot name="action" />
        </div>
    </div>
</template>
